import _ from 'lodash'
import { utils, coreConstants } from '@adc/ui-morpheus'

const {
  common: { convertToApiFormat },
  date: {
    API_LOCAL_DATE_FORMAT,
    convertISOTimeToString,
    convertDateFormat,
    formatDateApiToUi,
    formatDateUiToApi,
    formatDateTimeUiToApi,
  },
} = utils

const {
  statics: { UI_DATE_FORMAT },
} = coreConstants

const eventConfigFields = [
  'presentAsOnlineEvent',
  'comingSoon',
  'testEvent',
  'venueKnown',
  'webLiveStatus',
  'presale',
  'liveQual',
  'isOfferEnabled',
  'secondsBetweenAuctionEndDates',
  'isMatchBiddingEnabled',
]

const eventScheduleFields = ['campaignStartDate']

const VENUES = 'venues'
const EVENT_SCHEDULE = 'event_schedule'
const EVENT_CONFIGURATION = 'event_configuration'

const eventScheduleUiToApi = (values = {}) => {
  const eventSchedule = eventScheduleFields.reduce((schedule, field) => {
    let value = values[field]
    if (field === 'campaignStartDate') {
      value = formatDateUiToApi(value)
    }
    return {
      ...schedule,
      [field]: value,
    }
  }, {})
  return convertToApiFormat(eventScheduleFields, eventSchedule)
}

/**
 * Translation uiToApi for venues in event form
 * Here we want to send very lightweight venues to avoid passing massive payloads,
 * just pass what could change and values that help to infer the rest i.e venue_id, venue_template_id, venue_schedule
 */
const eventVenuesUiToApi = (currentVenues, initialVenues) => {
  /**
   * This is to avoid sending an update to venues
   * if there isn't any, preventing unwanted logs
   */
  return !_.isEqual(currentVenues, initialVenues)
    ? _.map(currentVenues, venue => {
        const apiVenue = _.pick(venue, [
          'venue_id',
          'venue_template_id',
          'venue_schedule',
        ])
        return apiVenue
      })
    : []
}

const uiToApi = (values, initialValues = {}) => {
  const baseEventKeys = _.keys(values).filter(
    key =>
      !eventConfigFields.includes(key) && !eventScheduleFields.includes(key)
  )

  const baseEventValues = convertToApiFormat(baseEventKeys, values)
  const eventConfigValues = convertToApiFormat(eventConfigFields, values)
  const eventScheduleValues = eventScheduleUiToApi(values)
  const eventVenuesValues = eventVenuesUiToApi(
    baseEventValues[VENUES],
    initialValues[VENUES]
  )

  return {
    ...baseEventValues,
    [VENUES]: eventVenuesValues,
    [EVENT_CONFIGURATION]: eventConfigValues,
    [EVENT_SCHEDULE]: eventScheduleValues,
  }
}

const apiToUi = values => {
  const {
    [EVENT_CONFIGURATION]: config,
    [EVENT_SCHEDULE]: schedule,
    ...restApiValues
  } = values
  const apiValues = {
    ...config,
    ...schedule,
    ...restApiValues,
  }

  const apiKeys = _.keys(apiValues)
  const result = apiKeys.reduce((uiValues, apiKey) => {
    const uiKey = _.camelCase(apiKey)
    let uiValue = apiValues[apiKey]

    if (apiKey === VENUES) {
      uiValue = uiValue.map(venue => {
        if (venue.venue_configuration) {
          venue.venue_configuration.web_live_status = venue.venue_configuration
            .web_live_status
            ? 'Active'
            : 'Inactive'
        }

        if (venue.venue_schedule) {
          venue.venue_schedule.bid_start_date_time = formatDateTimeUiToApi(
            convertDateFormat(
              venue.venue_schedule.bid_start_local_date,
              API_LOCAL_DATE_FORMAT,
              UI_DATE_FORMAT
            ),
            convertISOTimeToString(venue.venue_schedule.bid_start_local_time)
          )

          venue.venue_schedule.initial_bidding_end_date_time =
            formatDateTimeUiToApi(
              convertDateFormat(
                venue.venue_schedule.initial_bidding_end_local_date,
                API_LOCAL_DATE_FORMAT,
                UI_DATE_FORMAT
              ),
              convertISOTimeToString(
                venue.venue_schedule.initial_bidding_end_local_time
              )
            )
        }

        const address = utils.address.formatAddress(venue.venue_address)

        return {
          ...venue,
          ...address,
        }
      })
    } else if (uiKey === 'campaignStartDate') {
      uiValue = formatDateApiToUi(uiValue)
    }

    return {
      ...uiValues,
      [uiKey]: uiValue,
    }
  }, {})

  return result
}

const apiToUiDataRowsTranslation = dataRows => {
  if (!dataRows) return []
  return dataRows.map(row => {
    const newRow = _.cloneDeep(row)
    newRow.event_configuration.web_live_status = row.event_configuration
      .web_live_status
      ? 'Active'
      : 'Inactive'

    return newRow
  })
}

const apiToUiDataRowTranslation = row => apiToUiDataRowsTranslation([row])[0]

const uiToApiBulkFields = values => {
  return {
    event_configuration: convertToApiFormat(eventConfigFields, values),
  }
}

export {
  apiToUi,
  uiToApi,
  uiToApiBulkFields,
  apiToUiDataRowsTranslation,
  apiToUiDataRowTranslation,
}
